"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useVisibilityRef } from "@/hooks/useVisibilityRef";
import Image from "next/image";
import { useAuth } from "@/hooks/useAuth";
import { authFetch } from "@/lib/authFetch";
import { cdn } from "@/lib/cdn";
import { rewritePmImage } from "@/lib/pm-image";
import { FiTrendingUp, FiThumbsUp, FiThumbsDown, FiExternalLink, FiZap, FiShare2, FiMessageCircle, FiHeart, FiLoader } from "react-icons/fi";
import { MessageCircleIcon } from "@/app/components/icons/MessageCircleIcon";
import LoginPromptModal from "@/app/components/LoginPromptModal";
import { useT } from "@/hooks/useT";
import { generateGradient } from "@/app/components/GradientAvatar";
import PolymarketMarketCard from "@/app/components/PolymarketMarketCard";
import { extractPolymarketLinks } from "@/lib/polymarket-link-detector";
import { decodeHTMLEntities } from "@/lib/sanitize";

/**
 * PM avatar with gradient fallback — PM's S3 + image optimizer is unreliable
 * for profile images (intermittent 502 / 403). When the <img> fails to load,
 * swap in the same gradient we use when the user has no avatar at all.
 */
function AvatarWithFallback({ src, alt, seed }: { src: string; alt: string; seed: string }) {
  const [failed, setFailed] = useState(false);
  if (failed || !src) {
    const g = generateGradient(seed);
    return (
      <div
        className="w-full h-full rounded-[5px]"
        style={{ backgroundColor: g.backgroundColor, backgroundImage: g.backgroundImage }}
      />
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="w-full h-full rounded-[5px] object-cover"
      onError={() => setFailed(true)}
    />
  );
}

type MarketPreview = {
  id: string;
  question: string;
  description?: string;
  image?: string;
  outcomes?: string[];
  outcomePrices?: string[];
  volume?: number;
  liquidity?: number;
  endDate?: string;
  active?: boolean;
  closed?: boolean;
  url: string;
};

type TradeData = {
  outcome: string;
  shares: string;
  amount: string;
  price: string;
  orderId?: string;
  // Newer posts persist side via `side` ('BUY'|'SELL'); legacy posts had
  // it as `type`. Fall back to BUY when neither is present (matches the
  // activity feed's behavior for old rows).
  side?: 'BUY' | 'SELL' | string;
  type?: 'BUY' | 'SELL' | string;
};

interface Post {
  id: string;
  content: string;
  created_at: string;
  link_preview?: MarketPreview | null;
  trade_data?: TradeData | null;
  user_id: string | null;
  username: string | null;
  name: string | null;
  image: string | null;
  karma?: number;
  likes: number;
  dislikes: number;
  user_vote: number | null;
  source?: "frenflow" | "polymarket" | "predict" | "kalshi";
  pm_user_address?: string | null;
  pm_pseudonym?: string | null;
  pm_profile_image?: string | null;
  pm_reaction_count?: number;
  pm_positions?: Array<{ tokenId?: string; outcome?: string; size: number }> | null;
  /** Attached media (Giphy GIFs, image uploads). */
  pm_media?: Array<{ url: string; mediaType: string; altText?: string | null }> | null;
  /** Unified parent comment reference, prefixed by source
   *  (`pm_<id>`, `predict_<id>`, `kalshi_<id>`) to match the post id
   *  namespace. Populated for:
   *    - Polymarket replies (forwarded from parentCommentID by the API).
   *    - Predict / Kalshi replies (when the source exposes them).
   *    - FrenFlow replies posted via the Reply button on a foreign
   *      comment — persisted in posts.parent_external_id.
   *  When set, the thread renderer nests this post under the matching
   *  parent regardless of which source the parent comes from. */
  parent_external_id?: string | null;
  /** Kalshi-specific metadata: the sub-market picked + side + live ROI. */
  kalshi_market_title?: string | null;
  kalshi_side?: 'yes' | 'no' | null;
  kalshi_roi_pct?: number | null;
}

interface MarketTalksProps {
  marketSlug: string;
  marketQuestion: string;
  /** Polymarket parent event ID for fetching event-level comments */
  eventId?: string;
  /** Polymarket series ID for fetching series-level comments */
  seriesId?: string;
  /** Comma-separated crypto tickers like "ETH,BTC,SOL" */
  cryptoTickers?: string;
  /** Market source — enables fetching native comments from Predict.fun */
  marketSource?: "polymarket" | "kalshi" | "predict" | "hyperliquid";
  /** Predict.fun category slug for fetching native comments */
  predictCategoryId?: string;
  /** Kalshi event ticker for fetching native "ideas" from the social API */
  kalshiEventTicker?: string;
  /** Event-page context: when a specific sub-market is expanded, the
   *  inline composer links the post to that market (deeper link) instead
   *  of the event. Unset on market detail pages — composer uses the
   *  marketSlug/marketQuestion props directly. */
  selectedMarket?: { slug: string; question: string; image?: string | null } | null;
  /** Map from Polymarket CLOB tokenId → outcome display name. Lets us
   * render "90 Oklahoma City Thunder" under each PM commenter the way
   * polymarket.com does. */
  pmTokenIdToOutcome?: Record<string, string>;
  /** Mobile detail overlay puts a "Comments" tab above this component,
   *  which already labels the section. Pass true to drop the inner
   *  "Market Talks" title + chevron toggle and remove the card wrapper
   *  (background / border / padding) so the composer + thread render
   *  flush inside the parent tab body, matching Polymarket's mobile
   *  layout. Forces the body open on mobile (no collapse). */
  hideHeader?: boolean;
}

/** Truncate an Ethereum address for display */
function truncateAddress(address: string): string {
  if (address.length <= 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

/** Detect auto-generated "pseudonyms" that are actually raw wallet addresses
 * (0x + 40 hex, optionally with a -timestamp/suffix). Polymarket returns the
 * address verbatim as `profile.name` when a user hasn't set a handle, which
 * bypassed our truncateAddress fallback. Treat these as no-pseudonym so the
 * UI renders a short 0x1234…abcd form instead of a 60-char string. */
function isAddressLikePseudonym(str: string | null | undefined): boolean {
  if (!str) return false;
  return /^0x[a-fA-F0-9]{40}(?:[\s\-_.:].*)?$/i.test(str.trim());
}

export default function MarketTalks({ marketSlug, marketQuestion, eventId, seriesId, cryptoTickers, marketSource, predictCategoryId, kalshiEventTicker, selectedMarket, pmTokenIdToOutcome, hideHeader }: MarketTalksProps) {
  const t = useT();
  const router = useRouter();
  const { isLoading, isReady, isLoggedIn, currentUserId, currentWalletAddress } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [headerHovered, setHeaderHovered] = useState(false);
  // Mobile-only collapse — Market Talks is a tall section that pushes the
  // rest of the page down on phones. Default collapsed; user expands
  // explicitly. Always expanded on md+ (the toggle button is md:hidden).
  // hideHeader => permanently expanded. The "Comments" tab in the mobile
  // overlay is the parent collapse mechanism, so we disable the inner one.
  const [mobileExpanded, setMobileExpanded] = useState(!!hideHeader);
  const visRef = useVisibilityRef();
  // Inline composer state. Replaces the old CreatePostModal opener —
  // clicking "Share your take" now expands a textarea in place instead
  // of yanking the user out to a modal.
  const [composerContent, setComposerContent] = useState("");
  const [composerFocused, setComposerFocused] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  // Tracks which foreign comment the user is replying to. The id is
  // the unified `<source>_<rawId>` shape (e.g. `pm_2890891`,
  // `predict_abc`); the FrenFlow post created on submit carries this
  // as parent_external_id so the thread renderer can nest it under
  // the foreign parent on the next refresh.
  const [replyingTo, setReplyingTo] = useState<{ id: string; displayName: string } | null>(null);
  const [posting, setPosting] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // Post link preview.
  //
  // When a sub-market is expanded on an event page, we save the post with
  // the SUB-MARKET slug as `marketSlug` (stored in posts.market_slug). This
  // lets:
  //   (a) PolymarketMarketCard's `useMarketData(id)` fetch the right per-market
  //       prices + volume (instead of the event's aggregate $1B+ / 50-50), and
  //   (b) the activity enrichment lookup by `markets_cache.slug` find the
  //       market → derive event_slug → `marketPageUrl` produce the correct
  //       `/polymarket/event/<event>?market=<sub>&outcome=<y/n>` link on
  //       the individual post page.
  //
  // The event page's MarketTalks feed still surfaces sub-market posts because
  // `/api/posts/market` now matches `p.market_slug = event_slug` OR
  // `p.market_slug ∈ (markets_cache children of event_slug)` (backend change).
  const effectivePostSlug = selectedMarket?.slug || marketSlug;
  const effectivePostTitle = selectedMarket?.question || marketQuestion;
  const composerPreview = selectedMarket
    ? {
        id: selectedMarket.slug,
        slug: selectedMarket.slug,
        eventSlug: marketSlug,
        question: selectedMarket.question,
        image: selectedMarket.image || undefined,
        url: `https://frenflow.com/polymarket/event/${marketSlug}?market=${selectedMarket.slug}&outcome=yes`,
      }
    : {
        id: marketSlug,
        slug: marketSlug,
        question: marketQuestion,
        url: `https://frenflow.com/market/${marketSlug}`,
      };

  const handleComposerSubmit = async () => {
    if (!isLoggedIn) { setShowLoginModal(true); return; }
    const trimmed = composerContent.trim();
    if (!trimmed || posting) return;
    setPosting(true);
    try {
      const res = await fetch("/api/posts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content: trimmed,
          marketSlug: effectivePostSlug,
          marketTitle: effectivePostTitle,
          linkPreview: composerPreview,
          // Persist the parent reference when the user composed via
          // the Reply button. Backend validates the shape against
          // `^(pm|predict|kalshi)_[\\w.-]+$` and stores it on
          // posts.parent_external_id.
          parentExternalId: replyingTo?.id || undefined,
        }),
      });
      if (res.ok) {
        setComposerContent("");
        setComposerFocused(false);
        setReplyingTo(null);
        setRefreshTick((t) => t + 1);
      }
    } catch {
      // swallow — user can retry
    } finally {
      setPosting(false);
    }
  };

  useEffect(() => {
    let isFirst = true;
    async function fetchPosts() {
      try {
        if (isFirst) setLoading(true);

        // Fetch FrenFlow + Polymarket posts. Limit bumped from 10 → 30
        // so that PM reply threads have a fair chance of catching their
        // parent in the same page. With limit=10 a single popular
        // thread (parent + N replies) consumed the entire window and
        // pushed older parents out, breaking the visual nesting and
        // surfacing replies as orphans. 30 keeps the feed responsive
        // while comfortably covering most active threads.
        const params = new URLSearchParams({
          slug: marketSlug,
          question: marketQuestion,
          limit: "30",
        });
        if (eventId) params.set("eventId", eventId);
        if (seriesId) params.set("seriesId", seriesId);
        if (cryptoTickers) params.append("tickers", cryptoTickers);

        const frenflowPromise = fetch(`/api/posts/market?${params.toString()}`)
          .then(r => r.ok ? r.json() : { posts: [] })
          .then(r => (r.posts || []) as Post[])
          .catch(() => [] as Post[]);

        // Fetch Predict.fun native comments via our backend (cached, no CORS issues).
        // Triggered whenever a Predict ID is provided — independent of the
        // current market's source. Lets us merge cross-platform comments
        // when Predexon resolves a match for a PM/Kalshi page.
        const predictPromise = predictCategoryId
          ? fetch(`/api/markets/predict/${encodeURIComponent(predictCategoryId)}/comments?limit=15`)
            .then(r => r.ok ? r.json() : { comments: [] })
            .then(data => {
              return (data.comments || []).map((c: any): Post => ({
                id: `predict-${c.id}`,
                content: c.content || '',
                created_at: c.createdAt,
                user_id: null,
                username: null,
                name: c.account?.name || null,
                image: c.account?.imageUrl || null,
                karma: 0,
                likes: c.likeCount || 0,
                dislikes: 0,
                user_vote: null,
                source: 'predict' as const,
                pm_user_address: c.account?.address || null,
                pm_pseudonym: c.account?.name || null,
                pm_profile_image: c.account?.imageUrl || null,
                // Predict surfaces the parent via `parentComment { id }`
                // (extended in our backend GraphQL query). Prefix with
                // `predict-` to match this transform's id namespace so
                // the thread renderer can match.
                parent_external_id: c.parentComment?.id ? `predict-${c.parentComment.id}` : null,
              }));
            })
            .catch(() => [] as Post[])
          : Promise.resolve([] as Post[]);

        // Fetch Kalshi social "ideas" feed whenever a Kalshi ticker is
        // provided — independent of the current market's source. Kalshi
        // doesn't have comments per market; posts are event-level trader
        // takes with their position + ROI. Treat them like external
        // comments for the MarketTalks feed.
        const kalshiPromise: Promise<Post[]> = kalshiEventTicker
          ? fetch(`/api/markets/kalshi/social-timeline?event_ticker=${encodeURIComponent(kalshiEventTicker)}&limit=25`)
              .then(r => r.ok ? r.json() : { posts: [] })
              .then((data: any) => {
                const raw: any[] = Array.isArray(data?.posts) ? data.posts : [];
                return raw
                  .filter(p => (p.content && p.content.trim()) || (Array.isArray(p.media) && p.media.length > 0))
                  .map((p: any): Post => ({
                    id: `kalshi-${p.id}`,
                    content: p.content || '',
                    // created_ts is in microseconds
                    created_at: new Date(Math.floor(Number(p.created_ts) / 1000)).toISOString(),
                    user_id: null,
                    username: null,
                    name: p.nickname || null,
                    image: null,
                    karma: 0,
                    likes: Number(p.likes_count) || 0,
                    dislikes: 0,
                    user_vote: null,
                    source: 'kalshi' as const,
                    pm_user_address: null,
                    pm_pseudonym: p.nickname || null,
                    // Kalshi's social timeline returns `profile_image_url`
                    // (resolved to the public S3 avatar) enriched server-side.
                    pm_profile_image: p.profile_image_url || null,
                    pm_media: Array.isArray(p.media)
                      ? p.media
                          .filter((m: any) => m?.url)
                          .map((m: any) => ({
                            url: m.url,
                            mediaType: m.content_type?.includes('image') ? 'image' : (m.provider || 'gif'),
                            altText: null,
                          }))
                      : null,
                    kalshi_market_title: p.market_title || null,
                    kalshi_side: p.side === 'yes' || p.side === 'no' ? p.side : null,
                    kalshi_roi_pct: typeof p.percentage_roi === 'number' ? p.percentage_roi : null,
                  }));
              })
              .catch(() => [] as Post[])
          : Promise.resolve([] as Post[]);

        const [frenflowPosts, predictPosts, kalshiPosts] = await Promise.all([frenflowPromise, predictPromise, kalshiPromise]);

        // Merge and sort by date (newest first)
        const allPosts = [...frenflowPosts, ...predictPosts, ...kalshiPosts]
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        setPosts(allPosts);
      } catch (err) {
      } finally {
        if (isFirst) setLoading(false);
        isFirst = false;
      }
    }

    fetchPosts();

    // Refresh every 30 seconds (skip when tab is hidden)
    const interval = setInterval(() => { if (visRef.current) fetchPosts(); }, 30000);
    return () => clearInterval(interval);
  }, [marketSlug, marketQuestion, eventId, seriesId, cryptoTickers, marketSource, predictCategoryId, kalshiEventTicker, refreshTick]);

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const handleVote = async (postId: string, voteType: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    const post = posts.find((p) => p.id === postId);
    if (!post) return;

    const newVoteType = post.user_vote === voteType ? 0 : voteType;

    try {
      const response = await authFetch("/api/posts/like", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postId, voteType: newVoteType }),
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(
          posts.map((p) =>
            p.id === postId
              ? {
                  ...p,
                  likes: data.likes,
                  dislikes: data.dislikes,
                  user_vote: data.userVote,
                }
              : p
          )
        );
      }
    } catch (error) {
    }
  };

  const isExternalPost = (post: Post) =>
    post.source === "polymarket" || post.source === "predict" || post.source === "kalshi";

  /**
   * Reply button handler — pre-fills the composer with `@<name> `,
   * focuses the textarea, and stores the target id so the resulting
   * post is persisted as a reply (parent_external_id). On the next
   * refresh the new FrenFlow post nests right under the foreign
   * parent in the thread renderer. Replying to a FrenFlow-native post
   * still adds the @mention but leaves parent_external_id null
   * (FF-to-FF threading would need a `parent_post_id` column we don't
   * have yet — outside scope).
   */
  const handleReplyTo = (post: Post) => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    const isExternal = isExternalPost(post);
    const rawName = isExternal
      ? (post.pm_pseudonym && !isAddressLikePseudonym(post.pm_pseudonym)
          ? post.pm_pseudonym
          : (post.pm_user_address
              ? truncateAddress(post.pm_user_address)
              : (post.name || "user")))
      : (post.username || post.name || "user");
    // Collapse whitespace so the @mention reads as a single token. PM
    // pseudonyms can have spaces ("Alice Smith") which would break the
    // @mention convention.
    const handle = String(rawName).trim().replace(/\s+/g, "_");
    const prefix = `@${handle} `;
    setComposerContent((current) => (current.startsWith(prefix) ? current : `${prefix}${current}`));
    setComposerFocused(true);
    // Track the parent only when the target is a foreign comment whose
    // id matches our unified `<source>_<rawId>` shape. FF posts have
    // bare UUIDs and would fail the backend regex, so we just use the
    // @mention text for them.
    // PM ids use `pm_<rawId>` (underscore); Predict + Kalshi use the
    // hyphen-separated convention from their respective transforms.
    if (isExternal && /^(pm|predict|kalshi)[_-]/.test(post.id)) {
      setReplyingTo({ id: post.id, displayName: handle });
    } else {
      setReplyingTo(null);
    }
    // Defer focus so the textarea has time to render in its expanded
    // state (rows={3}) before we move the cursor — otherwise the
    // browser scroll jumps to the still-collapsed 1-row position.
    setTimeout(() => {
      composerRef.current?.focus();
      // Place cursor at the end so the user can type continuation.
      const len = composerRef.current?.value.length ?? 0;
      composerRef.current?.setSelectionRange(len, len);
      composerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 30);
  };

  // Group Polymarket replies under their parent comment so the feed
  // reads like polymarket.com instead of the previous flat ordering
  // where a reply appeared as an unrelated post N rows away. We keep
  // the API's overall DESC sort for parents, then append each parent's
  // replies (oldest-first within the thread, the conventional reply
  // order). Replies whose parent isn't on this page are appended at
  // the bottom as flat orphans (no indentation, since there's no
  // parent above them to nest under).
  const { threadedPosts, nestedReplyIds } = useMemo(() => {
    if (!posts.length) return { threadedPosts: posts, nestedReplyIds: new Set<string>() };
    const repliesByParent = new Map<string, Post[]>();
    const topLevel: Post[] = [];
    for (const p of posts) {
      const parent = p.parent_external_id || null;
      if (parent) {
        const arr = repliesByParent.get(parent) || [];
        arr.push(p);
        repliesByParent.set(parent, arr);
      } else {
        topLevel.push(p);
      }
    }
    for (const arr of repliesByParent.values()) {
      arr.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    }
    const out: Post[] = [];
    const placedParents = new Set<string>();
    const nested = new Set<string>();
    for (const parent of topLevel) {
      out.push(parent);
      placedParents.add(parent.id);
      const kids = repliesByParent.get(parent.id);
      if (kids) {
        for (const k of kids) {
          out.push(k);
          nested.add(k.id);
        }
      }
    }
    for (const [parentId, kids] of repliesByParent) {
      if (!placedParents.has(parentId)) out.push(...kids);
    }
    return { threadedPosts: out, nestedReplyIds: nested };
  }, [posts]);

  // Cut event-page scroll: render only the first few top-level comments, with
  // a "View all" that expands the rest in-place. Replies of a shown parent
  // stay nested with it; collapsing only counts top-level (non-reply) posts.
  const VISIBLE_PARENTS = 3;
  const [showAllComments, setShowAllComments] = useState(false);
  const totalParents = useMemo(
    () => threadedPosts.reduce((n, p) => n + (nestedReplyIds.has(p.id) ? 0 : 1), 0),
    [threadedPosts, nestedReplyIds]
  );
  const visiblePosts = useMemo(() => {
    if (showAllComments || totalParents <= VISIBLE_PARENTS) return threadedPosts;
    let parents = 0;
    const out: Post[] = [];
    for (const p of threadedPosts) {
      if (!nestedReplyIds.has(p.id)) {
        if (parents >= VISIBLE_PARENTS) break;
        parents++;
      }
      out.push(p);
    }
    return out;
  }, [threadedPosts, nestedReplyIds, showAllComments, totalParents]);

  if (loading) {
    return (
      <div
        className={hideHeader
          ? "transition-colors"
          : `bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors md:p-6 md:min-h-[340px] ${
              mobileExpanded ? 'p-3 min-h-[340px]' : 'p-3 min-h-0'
            }`}
        onMouseEnter={() => setHeaderHovered(true)}
        onMouseLeave={() => setHeaderHovered(false)}
      >
        {!hideHeader && (
          <div className="flex items-center gap-1.5 md:gap-2 mb-3 md:mb-4">
            <MessageCircleIcon size={20} className="text-gray-400 dark:text-gray-500" isHovered={headerHovered} />
            <h3 className="font-monument text-base md:text-lg text-gray-900 dark:text-white">
              {t("section.marketTalks")}
            </h3>
          </div>
        )}
        <div className="space-y-3 md:space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex gap-2 md:gap-2.5 px-2 py-2 md:px-3 md:py-2.5 border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] animate-pulse">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-gray-200 dark:bg-[#0f0f12] rounded-[5px]" />
              <div className="flex-1">
                <div className="h-3 md:h-4 bg-gray-200 dark:bg-[#0f0f12] rounded w-1/4 mb-2" />
                <div className="h-2.5 md:h-3 bg-gray-200 dark:bg-[#0f0f12] rounded w-full mb-1" />
                <div className="h-2.5 md:h-3 bg-gray-200 dark:bg-[#0f0f12] rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        message="Sign in to join the discussion"
      />
      <div
        // hideHeader => no card wrapper, no padding, no border. The mobile
        // overlay's "Comments" tab is the visual container; rendering the
        // composer + thread flush on the parent surface matches Polymarket.
        className={hideHeader
          ? "transition-colors"
          : `bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors md:p-6 md:min-h-[340px] ${
              mobileExpanded ? 'p-3 min-h-[340px]' : 'p-3 min-h-0'
            }`}
        onMouseEnter={() => setHeaderHovered(true)}
        onMouseLeave={() => setHeaderHovered(false)}
      >
        {!hideHeader && (
          <button
            type="button"
            onClick={() => setMobileExpanded((v) => !v)}
            // Acts as a toggle on mobile, plain header on md+ (cursor stays
            // default, click is a no-op there because the body is always shown).
            // Margin-bottom collapses to 0 when contraído on mobile so the
            // section doesn't reserve body padding it isn't using.
            className={`w-full flex items-center justify-between md:mb-4 md:cursor-default md:pointer-events-none ${
              mobileExpanded ? 'mb-3' : 'mb-0'
            }`}
            aria-expanded={mobileExpanded}
            aria-controls="market-talks-body"
          >
            <div className="flex items-center gap-1.5 md:gap-2">
              <MessageCircleIcon size={20} className="text-gray-400 dark:text-gray-500" isHovered={headerHovered} />
              <h3 className="font-monument text-base md:text-lg text-gray-900 dark:text-white">
                {t("section.marketTalks")}
              </h3>
              {posts.length > 0 && (
                <span className="md:hidden text-[11px] font-medium text-gray-500 dark:text-gray-400 tabular-nums">
                  {posts.length}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {cryptoTickers && (
                <div className="hidden md:flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <FiTrendingUp className="w-3 h-3" />
                  <span className="font-inter-tight">{cryptoTickers.split(",").map(t => `$${t}`).join(" ")}</span>
                </div>
              )}
              {/* Chevron — mobile only. md+ shows the body unconditionally. */}
              <svg
                className={`md:hidden w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform ${mobileExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
        )}

        {/* Body — wraps composer + post list. Hidden on mobile until
            mobileExpanded toggles true (or always visible when the
            parent hides the header — mobile overlay case). */}
        <div
          id="market-talks-body"
          className={hideHeader
            ? "block"
            : `${mobileExpanded ? 'block' : 'hidden'} md:block`}
        >

        {/* Inline composer — no modal. Textarea stays collapsed (1 row)
            until focused/typed, then reveals the context pill + action row.
            Submit links the post to the deepest available context
            (selected sub-market > event/market page). */}
        <div className="mb-3 md:mb-4">
          {replyingTo && (
            <div className="mb-1.5 flex items-center justify-between gap-2 px-2.5 py-1 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] text-[11px] font-satoshi text-gray-600 dark:text-gray-400">
              <span className="truncate">
                {t("marketTalks.replyingTo")} <span className="font-semibold text-gray-900 dark:text-white">@{replyingTo.displayName}</span>
              </span>
              <button
                type="button"
                onClick={() => {
                  setReplyingTo(null);
                  // Strip the leading @mention prefix so the composer doesn't
                  // keep the visual cue of a reply once the user cancels.
                  setComposerContent((current) => current.replace(/^@\S+\s+/, ""));
                }}
                className="text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors flex-shrink-0"
                aria-label={t("marketTalks.cancelReply")}
              >
                ✕
              </button>
            </div>
          )}
          <textarea
            ref={composerRef}
            value={composerContent}
            onChange={(e) => setComposerContent(e.target.value)}
            onFocus={() => {
              if (!isLoggedIn) { setShowLoginModal(true); return; }
              setComposerFocused(true);
            }}
            placeholder={t("marketTalks.placeholder")}
            maxLength={500}
            rows={composerFocused || composerContent ? 3 : 1}
            className="w-full p-2.5 md:p-3 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] md:rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] focus:border-gray-400 dark:focus:border-gray-500 outline-none resize-none text-xs md:text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 transition-colors"
          />
          {(composerFocused || composerContent) && (
            <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
              {/* Context pill — shows the sub-market image (when one is
                  expanded) so the user sees exactly what their post will be
                  tagged to. Neutral gray surface, no neon-lime borders. */}
              <div className="flex items-center gap-1.5 text-[10px] md:text-[11px] text-gray-500 dark:text-gray-400 min-w-0">
                <FiMessageCircle className="w-3 h-3 flex-shrink-0" />
                <span className="flex-shrink-0">{t("marketTalks.on")}</span>
                <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] text-gray-900 dark:text-gray-100 font-medium truncate max-w-[260px] md:max-w-[380px]">
                  {composerPreview.image && (
                    <img
                      src={rewritePmImage(composerPreview.image)}
                      alt=""
                      className="w-4 h-4 rounded-sm object-cover flex-shrink-0"
                    />
                  )}
                  <span className="truncate">{composerPreview.question}</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => { setComposerContent(""); setComposerFocused(false); }}
                  className="px-3 py-1.5 text-[11px] md:text-xs font-inter-tight font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                >
                  {t("marketTalks.cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleComposerSubmit}
                  disabled={!composerContent.trim() || posting}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 text-[11px] md:text-xs font-inter-tight font-bold bg-gray-900 dark:bg-white text-white dark:text-black rounded-[5px] hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors active:scale-95"
                >
                  {posting ? (<><FiLoader className="w-3 h-3 animate-spin" />{t("marketTalks.posting")}</>) : t("marketTalks.post")}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-1.5 md:space-y-2 max-h-[600px] overflow-y-auto">
          {posts.length === 0 ? (
            <div className="text-center py-8">
              <FiMessageCircle className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="font-satoshi text-sm text-gray-500 dark:text-gray-400">
                {t("marketTalks.empty.title")}
              </p>
              <p className="font-satoshi text-xs text-gray-400 dark:text-gray-500 mt-1">
                {t("marketTalks.empty.subtitle")}
              </p>
            </div>
          ) : (
            visiblePosts.map((post) => {
              const isExternal = isExternalPost(post);
              // Indent + slim border only when the reply's parent is on
              // the same page (i.e. visible directly above). Orphan
              // replies — parent not in our paginated slice — render
              // flat so they don't dangle under nothing.
              const isReply = nestedReplyIds.has(post.id);
              const replyIndent = isReply ? "ml-6 md:ml-8 border-l-2 border-gray-200 dark:border-[#1A1A1A] pl-3" : "";
              const baseCard = `flex gap-2 md:gap-2.5 px-2 py-2 md:px-3 md:py-2.5 border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] hover:bg-gray-50 dark:hover:bg-[#0f0f12]/50 transition-colors`;

              // PM posts are not clickable (no internal post page)
              const Wrapper = isExternal ? "div" : Link;
              const wrapperProps = isExternal
                ? { className: `${baseCard} ${replyIndent}` }
                : { href: `/post/${post.id}`, className: `${baseCard} block ${replyIndent}` };

              return (
                <Wrapper key={post.id} {...(wrapperProps as any)}>
                  {/* Avatar */}
                  {isExternal ? (
                    <div
                      onClick={post.pm_user_address ? (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        router.push(`/traders/${post.pm_user_address}`);
                      } : undefined}
                      className={`flex-shrink-0 ${post.pm_user_address ? "cursor-pointer" : "cursor-default"}`}
                    >
                      <div className="w-8 h-8 md:w-9 md:h-9 rounded-[5px] overflow-hidden">
                        {post.pm_profile_image ? (
                          <AvatarWithFallback
                            src={rewritePmImage(post.pm_profile_image)}
                            alt={post.pm_pseudonym || "PM User"}
                            seed={post.pm_user_address || post.pm_pseudonym || post.id}
                          />
                        ) : (
                          // No avatar on record → deterministic gradient
                          // seeded by the user's wallet / pseudonym so the
                          // fallback matches the /traders/@username profile
                          // gradient rather than the old two-letter initials.
                          (() => {
                            const g = generateGradient(post.pm_user_address || post.pm_pseudonym || post.id);
                            return (
                              <div
                                className="w-full h-full rounded-[5px]"
                                style={{
                                  backgroundColor: g.backgroundColor,
                                  backgroundImage: g.backgroundImage,
                                }}
                              />
                            );
                          })()
                        )}
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        router.push(`/profile/${post.username}`);
                      }}
                      className="flex-shrink-0 cursor-pointer"
                    >
                      <div className="w-8 h-8 md:w-9 md:h-9 rounded-[5px] bg-gray-300 dark:bg-gray-600 p-0.5">
                        <Image
                          src={post.image || cdn("/frenpfp.png")}
                          alt={post.username || "User"}
                          width={40}
                          height={40}
                          className="w-full h-full rounded-[5px] object-cover"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    {/* User Info */}
                    <div className="flex flex-col md:flex-row md:items-center md:gap-2 mb-0.5 md:mb-1">
                      {isExternal ? (() => {
                        const cleanPseudonym = isAddressLikePseudonym(post.pm_pseudonym) ? null : post.pm_pseudonym;
                        const displayName = cleanPseudonym
                          || (post.pm_user_address ? truncateAddress(post.pm_user_address) : "Anonymous");
                        return (
                        <span
                          onClick={post.pm_user_address ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            router.push(`/traders/${post.pm_user_address}`);
                          } : undefined}
                          className={`font-satoshi font-bold text-sm md:text-base text-gray-900 dark:text-white truncate max-w-[140px] md:max-w-none ${post.pm_user_address && !cleanPseudonym ? "font-mono text-xs" : ""} ${post.pm_user_address ? "hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer" : "cursor-default"}`}
                        >
                          {displayName}
                        </span>
                        );
                      })() : (
                        <span
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            router.push(`/profile/${post.username}`);
                          }}
                          className="font-satoshi font-bold text-sm md:text-base text-gray-900 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer dark:text-white truncate max-w-[140px] md:max-w-none"
                        >
                          {post.name || post.username || t("marketTalks.anonymous")}
                        </span>
                      )}
                      <div className="flex items-center gap-1.5 md:gap-2">
                        {/* Source badge — brand logo on a colored chip
                            (same palette as WalletAddress chips so the
                            visual language is consistent across the app). */}
                        {isExternal && (() => {
                          const meta = post.source === "kalshi"
                            ? { src: "/logos/kalshi/k-icon.svg", bg: "#09C285", label: "Kalshi" }
                            : post.source === "predict"
                              ? { src: "/logos/predictfun/Predict-Icon-PB.png", bg: "transparent", label: "Predict.fun" }
                              : { src: "/logos/polymarket/icon-white.png", bg: "#2E5CFF", label: "Polymarket" };
                          return (
                            <span
                              title={meta.label}
                              className="inline-flex items-center justify-center w-4 h-4 rounded-[3px] flex-shrink-0"
                              style={{ backgroundColor: meta.bg }}
                            >
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img src={meta.src} alt={meta.label} className="w-3 h-3 object-contain" />
                            </span>
                          );
                        })()}
                        {/* Polymarket position badge — skin in the game.
                            Shows the single position with the largest size on
                            this event ("43.3K Boston Celtics"), matching how
                            polymarket.com surfaces it under each comment. */}
                        {post.source === 'polymarket' && post.pm_positions && post.pm_positions.length > 0 && (() => {
                          // Resolve tokenId → outcome name via the event map.
                          // Keep only positions whose tokenId exists in the
                          // current event (drops positions on unrelated
                          // events from the same user).
                          const resolved = post.pm_positions
                            .map(p => ({
                              outcome: p.outcome || (p.tokenId && pmTokenIdToOutcome ? pmTokenIdToOutcome[p.tokenId] : undefined),
                              size: p.size,
                            }))
                            .filter(p => !!p.outcome);
                          if (resolved.length === 0) return null;
                          const top = resolved.slice().sort((a, b) => b.size - a.size)[0];
                          const sizeLabel = top.size >= 1000
                            ? `${(top.size / 1000).toFixed(top.size >= 10000 ? 1 : 1)}K`
                            : `${Math.round(top.size)}`;
                          return (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 font-medium font-inter-tight">
                              {sizeLabel} {top.outcome}
                            </span>
                          );
                        })()}
                        {/* Kalshi position badge — side + market + live ROI */}
                        {post.source === "kalshi" && post.kalshi_side && post.kalshi_market_title && (
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded font-medium font-inter-tight ${
                              post.kalshi_side === 'yes'
                                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
                                : 'bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400'
                            }`}
                          >
                            {post.kalshi_side.toUpperCase()} · {post.kalshi_market_title}
                            {typeof post.kalshi_roi_pct === 'number' && post.kalshi_roi_pct !== 0 && (
                              <span className="ml-1 opacity-75">
                                {post.kalshi_roi_pct > 0 ? '+' : ''}
                                {(post.kalshi_roi_pct * 100).toFixed(0)}%
                              </span>
                            )}
                          </span>
                        )}
                        {!isExternal && post.username && (
                          <span
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              router.push(`/profile/${post.username}`);
                            }}
                            className="text-xs md:text-sm font-satoshi text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer"
                          >
                            @{post.username}
                          </span>
                        )}
                        {!isExternal && (
                          <span className="flex items-center gap-0.5 md:gap-1 text-[10px] md:text-xs text-gray-500 dark:text-gray-400 font-satoshi font-bold">
                            <FiZap className="w-2.5 h-2.5 md:w-3 md:h-3" />
                            {post.karma || 0}
                          </span>
                        )}
                        <span className="text-[10px] md:text-xs font-satoshi text-gray-400 dark:text-gray-500">
                          {formatTimeAgo(post.created_at)}
                        </span>
                      </div>
                    </div>

                    {/* Post Content */}
                    <p className="text-gray-700 dark:text-gray-100 whitespace-pre-wrap break-words font-satoshi text-xs md:text-sm">
                      {decodeHTMLEntities(post.content).split(/(https?:\/\/[^\s<]+)/g).map((part, i) => {
                        if (part.match(/^https?:\/\//)) {
                          try {
                            const urlObj = new URL(part);
                            const shortText = urlObj.hostname.replace("www.", "");
                            return (
                              <span
                                key={i}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  window.open(part, "_blank");
                                }}
                                className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                              >
                                {shortText}
                              </span>
                            );
                          } catch {
                            return part;
                          }
                        }
                        return part;
                      })}
                    </p>

                    {/* Attached media (Giphy gifs, image uploads) from PM.
                        Inline render only — we deliberately skip any
                        click-to-open behavior to keep users on FrenFlow. */}
                    {Array.isArray(post.pm_media) && post.pm_media.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {post.pm_media.slice(0, 4).map((m, i) => (
                          <img
                            key={i}
                            src={rewritePmImage(m.url, 'hero')}
                            alt={m.altText || ''}
                            className="max-w-[240px] max-h-[240px] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] object-contain bg-gray-50 dark:bg-[#0f0f12] pointer-events-none select-none"
                            loading="lazy"
                          />
                        ))}
                      </div>
                    )}

                    {/* Trade Badge & Market Card (FrenFlow posts only) */}
                    {!isExternal && (() => {
                      const polymarketLinks = extractPolymarketLinks(post.content);

                      // If there are Polymarket links in the content, show those
                      if (polymarketLinks.length > 0) {
                        return (
                          <div
                            className="mt-3 space-y-3"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            {polymarketLinks.map((link, index) => (
                              <PolymarketMarketCard key={index} slug={link.slug} />
                            ))}
                          </div>
                        );
                      }

                      // If there's a link_preview with a Polymarket market ID (from trade shares)
                      if (post.link_preview?.id) {
                        return (
                          <div
                            className="mt-3 relative"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            {/* Trade pills — same clean Bought/Sold + outcome
                                + price + size set used by /activity, replacing
                                the old neon-bordered "TRADE" pill so the same
                                trade reads identically across both surfaces.
                                Designed to match ActivityFeed's user-post
                                trade row at the same zoom/density. */}
                            {post.trade_data && (() => {
                              const td = post.trade_data;
                              const isSell = (td.side || td.type)?.toUpperCase() === 'SELL';
                              const isYes = td.outcome.toLowerCase().includes('yes') || td.outcome.toLowerCase().includes('up');
                              const priceCents = parseFloat(td.price || "0");
                              const amountUsd = parseFloat(td.amount || "0");
                              return (
                                <div className="mb-2.5 flex items-center gap-1.5 flex-wrap">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-[5px] text-[11px] font-semibold ${isSell ? "bg-red-500/10 text-red-500" : "bg-emerald-500/10 text-emerald-500"}`}>
                                    {isSell ? t("marketTalks.sold") : t("marketTalks.bought")}
                                  </span>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-[5px] text-[11px] font-semibold ${isYes ? "bg-emerald-500/10 text-emerald-500" : "bg-red-500/10 text-red-500"}`}>
                                    {td.outcome}
                                  </span>
                                  {priceCents > 0 && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-[5px] text-[11px] font-semibold bg-gray-100 dark:bg-[#0f0f12] text-gray-600 dark:text-gray-400">
                                      @ {priceCents.toFixed(priceCents < 1 ? 1 : 0)}¢
                                    </span>
                                  )}
                                  {amountUsd > 0 && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-[5px] text-[11px] font-semibold bg-gray-100 dark:bg-[#0f0f12] text-gray-600 dark:text-gray-400">
                                      ${amountUsd.toFixed(amountUsd < 10 ? 0 : 0)}
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                            <PolymarketMarketCard
                              slug={post.link_preview.id}
                              fallbackData={{
                                question: post.link_preview.question,
                                title: (post.link_preview as any).title,
                                image: post.link_preview.image,
                                outcomes: post.link_preview.outcomes,
                                outcomePrices: post.link_preview.outcomePrices,
                                url: post.link_preview.url,
                                eventSlug: (post.link_preview as any).eventSlug || undefined,
                              }}
                            />
                          </div>
                        );
                      }

                      // Simple trade pills fallback when there's no market
                      // link_preview attached — same clean style as the
                      // primary block above.
                      if (post.trade_data && !post.link_preview) {
                        const td = post.trade_data;
                        const isSell = (td.side || td.type)?.toUpperCase() === 'SELL';
                        const isYes = td.outcome.toLowerCase().includes('yes') || td.outcome.toLowerCase().includes('up');
                        const priceCents = parseFloat(td.price || "0");
                        const amountUsd = parseFloat(td.amount || "0");
                        return (
                          <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-[5px] text-[11px] font-semibold ${isSell ? "bg-red-500/10 text-red-500" : "bg-emerald-500/10 text-emerald-500"}`}>
                              {isSell ? t("marketTalks.sold") : t("marketTalks.bought")}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-[5px] text-[11px] font-semibold ${isYes ? "bg-emerald-500/10 text-emerald-500" : "bg-red-500/10 text-red-500"}`}>
                              {td.outcome}
                            </span>
                            {priceCents > 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-[5px] text-[11px] font-semibold bg-gray-100 dark:bg-[#0f0f12] text-gray-600 dark:text-gray-400">
                                @ {priceCents.toFixed(priceCents < 1 ? 1 : 0)}¢
                              </span>
                            )}
                            {amountUsd > 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-[5px] text-[11px] font-semibold bg-gray-100 dark:bg-[#0f0f12] text-gray-600 dark:text-gray-400">
                                ${amountUsd.toFixed(0)}
                              </span>
                            )}
                          </div>
                        );
                      }

                      return null;
                    })()}

                    {/* Footer: Like/Dislike for FrenFlow native posts.
                        External comments (PM/Kalshi/Predict) only get
                        a Reply button — likes/dislikes implied an
                        interactivity we can't support cross-platform. */}
                    {isExternal ? (
                      <div className="flex items-center gap-2 mt-1.5 pt-1.5 border-t border-gray-200 dark:border-[#1A1A1A]" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleReplyTo(post); }}
                          className="flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1 md:py-1.5 rounded-[5px] text-xs md:text-sm font-satoshi font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#0f0f12] hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                          <FiMessageCircle className="w-3.5 h-3.5 md:w-4 md:h-4" />
                          <span>{t("marketTalks.reply")}</span>
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 md:gap-3 mt-1.5 pt-1.5 border-t border-gray-200 dark:border-[#1A1A1A]" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                        <button
                          onClick={(e) => handleVote(post.id, 1, e)}
                          className={`flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1 md:py-1.5 rounded-[5px] text-xs md:text-sm font-satoshi font-bold transition-colors ${
                            post.user_vote === 1
                              ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                              : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#0f0f12] hover:text-gray-900 dark:hover:text-white"
                          }`}
                        >
                          <FiThumbsUp
                            className={`w-3.5 h-3.5 md:w-4 md:h-4 ${
                              post.user_vote === 1 ? "fill-current" : ""
                            }`}
                          />
                          <span>{Number(post.likes) || 0}</span>
                        </button>
                        <button
                          onClick={(e) => handleVote(post.id, -1, e)}
                          className={`flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1 md:py-1.5 rounded-[5px] text-xs md:text-sm font-satoshi font-bold transition-colors ${
                            post.user_vote === -1
                              ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                              : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#0f0f12] hover:text-gray-900 dark:hover:text-white"
                          }`}
                        >
                          <FiThumbsDown
                            className={`w-3.5 h-3.5 md:w-4 md:h-4 ${
                              post.user_vote === -1 ? "fill-current" : ""
                            }`}
                          />
                          <span>{Number(post.dislikes) || 0}</span>
                        </button>
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleReplyTo(post); }}
                          className="flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1 md:py-1.5 rounded-[5px] text-xs md:text-sm font-satoshi font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#0f0f12] hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                          <FiMessageCircle className="w-3.5 h-3.5 md:w-4 md:h-4" />
                          <span>{t("marketTalks.reply")}</span>
                        </button>
                        <span className="flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1 md:py-1.5 rounded-[5px] text-xs md:text-sm font-satoshi font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#0f0f12] hover:text-gray-900 dark:hover:text-white transition-colors ml-auto cursor-pointer">
                          <FiShare2 className="w-3.5 h-3.5 md:w-4 md:h-4" />
                        </span>
                      </div>
                    )}
                  </div>
                </Wrapper>
              );
            })
          )}
        </div>

        {!showAllComments && totalParents > VISIBLE_PARENTS && (
          <button
            type="button"
            onClick={() => setShowAllComments(true)}
            className="mt-3 w-full py-2 text-center text-xs font-inter-tight font-semibold text-gray-600 dark:text-gray-400 bg-white dark:bg-[#0a0a0b] border border-gray-200 dark:border-[#1A1A1A] rounded-[3px] hover:bg-gray-50 dark:hover:bg-[#0f0f12] transition-colors"
          >
            {t("marketTalks.viewAll")} ({totalParents})
          </button>
        )}

        {posts.length > 0 && (showAllComments || totalParents <= VISIBLE_PARENTS) && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-[#1A1A1A]">
            <Link
              href="/activity"
              className="font-inter-tight text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors font-medium flex items-center justify-center gap-1"
            >
              {t("marketTalks.viewAll")}
              <FiExternalLink className="w-3 h-3" />
            </Link>
          </div>
        )}
        </div>{/* /market-talks-body */}
      </div>
    </>
  );
}
